import type { SyntheticEvent } from 'react';
import { useCallback, useState } from 'react';
import { buscarLotes } from '../../api/lotes.js';
import { useConsulta } from '../../api/useConsulta.js';
import { Botao } from '../../componentes/Botao.js';
import { CampoTexto } from '../../componentes/Campo.js';
import { EstadoVazio } from '../../componentes/EstadoVazio.js';
import { IconeBusca } from '../../componentes/Icones.js';
import { Resultado } from '../../componentes/Resultado.js';
import type { LoteDaEtiqueta } from './etiqueta-lote.js';
import { linhasDeDetalhe, lotesPorEtiqueta, validarCodLot } from './etiqueta-lote.js';
import './BuscaNoCadastro.css';

/** Quantas linhas a busca traz: é um seletor, não uma listagem. */
const TAMANHO_DA_BUSCA = 8;

type Props = {
  /** Chaves (produto + derivação + lote) já na lista de impressão. */
  readonly naLista: readonly string[];
  readonly aoEscolher: (lote: LoteDaEtiqueta) => void;
  /** Termo já buscado ao montar. */
  readonly buscaInicial?: string;
};

function ItemDoLote({
  item,
  naLista,
  aoEscolher,
}: {
  readonly item: LoteDaEtiqueta;
  readonly naLista: boolean;
  readonly aoEscolher: (lote: LoteDaEtiqueta) => void;
}) {
  const { lote } = item;
  const recusa = validarCodLot(lote.codLot);

  return (
    <li className="busca-cadastro__item busca-cadastro__item--lote">
      <span className="busca-cadastro__codigo">{lote.codLot}</span>
      <span className="busca-cadastro__nome">
        {[...linhasDeDetalhe(lote), lote.descricaoProduto].filter(Boolean).join(' · ')}
        <span className="busca-cadastro__local">{item.locais.join(', ')}</span>
      </span>
      {recusa !== null ? (
        <span className="busca-cadastro__recusa">{recusa}</span>
      ) : naLista ? (
        <span className="busca-cadastro__na-lista">Na lista</span>
      ) : (
        <Botao
          altura="pequena"
          aria-label={`Adicionar lote ${lote.codLot} de ${lote.codPro}`}
          onClick={() => {
            aoEscolher(item);
          }}
        >
          Adicionar
        </Botao>
      )}
    </li>
  );
}

/**
 * Escolher lotes entre os sincronizados do ERP (`GET /lotes`).
 *
 * A busca só sai quando a pessoa pede, e acha pelo código do lote OU do
 * produto. Lote que não cabe no QR aparece com o motivo e sem botão: a
 * etiqueta dele não existiria.
 */
export function BuscaDeLotes({ naLista, aoEscolher, buscaInicial = '' }: Props) {
  const [texto, definirTexto] = useState(buscaInicial);
  const [termo, definirTermo] = useState(buscaInicial);

  const carregar = useCallback(
    () => buscarLotes({ busca: termo, tamanho: TAMANHO_DA_BUSCA }),
    [termo],
  );
  const consulta = useConsulta(termo === '' ? null : carregar);

  function buscar(evento: SyntheticEvent<HTMLFormElement>) {
    evento.preventDefault();
    definirTermo(texto.trim());
  }

  return (
    <div className="busca-cadastro">
      <form className="busca-cadastro__formulario" onSubmit={buscar}>
        <CampoTexto
          rotulo="Buscar lote"
          valor={texto}
          aoMudar={definirTexto}
          ajuda="Código do lote ou do produto, como está no ERP"
        />
        <Botao type="submit">Buscar</Botao>
      </form>

      {termo !== '' && (
        <Resultado
          consulta={consulta}
          espera="Buscando lotes…"
          estaVazio={(dados) => dados.itens.length === 0}
          vazio={
            <EstadoVazio
              tom="neutro"
              icone={<IconeBusca tamanho={22} />}
              titulo="Nenhum lote encontrado"
              detalhe="Os lotes vêm do ERP pela carga de lotes, só os que têm saldo. Produto sem lote usa a etiqueta de produto."
            />
          }
        >
          {(dados) => (
            <ul className="busca-cadastro__resultados" aria-label="Lotes encontrados">
              {lotesPorEtiqueta(dados.itens).map((item) => (
                <ItemDoLote
                  key={item.chave}
                  item={item}
                  naLista={naLista.includes(item.chave)}
                  aoEscolher={aoEscolher}
                />
              ))}
            </ul>
          )}
        </Resultado>
      )}
    </div>
  );
}
